// Deep-link into Gmail at the exact underlying message, so the user can hit
// Reply without hunting for it in their inbox (Heather's ask, 2026-08-14).
//
// ACCOUNT SELECTION (Heather + Ken live A/B/C test, 2026-08-21). Select the
// mailbox by ADDRESS via `?authuser=<email>`. We must NOT use `/mail/u/<email>/`:
// that path segment takes a 0-based account INDEX (`/u/0/`, `/u/1/`), never an
// address — putting an email there is malformed and 404s. That was the exact bug
// Heather hit; it only ever "worked" when it fell through to `/u/0/` and her
// account happened to be first. `authuser=<email>` is the one correct by-address
// selector (form A failed; forms B and C, both authuser-based, worked live).
//
// TARGET. Preferred = the RFC822 Message-ID search (`#search/rfc822msgid:`),
// which resolves the EXACT message the user clicked. We do NOT prefer the thread
// deep-link `#all/<gmail_thread_id>`: that only identifies the CONVERSATION, so
// on a multi-message thread Gmail lands on whichever message it picks. When two
// messages of one thread are filed into different matters, that put the user on
// the wrong email in the wrong matter (Maria: clicked EMAL-014596 in M-600,
// landed on EMAL-013000 which lives in M-572 — same thread 1a029a9cfa98bfaa;
// Emerson 2026-09-02). The Message-ID form opens a one-row search result rather
// than the threaded view (tested form B) — the accepted cost of landing on the
// right message. Thread id (Gmail's 16-hex web id, verified against a live
// thread) is the FALLBACK, used only when a message has no Message-ID.

export function gmailReplyUrl(a: {
  message_id_header?: string | null
  gmail_thread_id?: string | null
  source_account_email?: string | null
  source_type?: string | null
}, userEmail?: string | null): string | null {
  // Uploaded (demo-tenant) emails have no real Gmail message behind them, so a
  // reply link would 404. Suppress it — real synced mail is unaffected (Ken
  // 2026-08-24, demo prep).
  if ((a.source_type || '') === 'upload') return null
  // Pin to the CLICKING USER'S own Gmail account (they're signed into it), NOT
  // the sync-source mailbox. MOVU syncs from both Heather's and Maria's boxes and
  // cross-mailbox dedup means source_account_email is often the OTHER person's
  // address — which would select a mailbox the clicker isn't signed into.
  const acct = ((userEmail || a.source_account_email) || '').trim()
  const isEmail = /^[^\s/@]+@[^\s/@]+$/.test(acct)

  // Insert the address raw: the regex above guarantees no URL-breaking chars, and
  // Gmail wants the bare `@` (matches the tested-working string). When we don't
  // know an address, fall back to `/u/0/` (default account).
  const base = isEmail
    ? `https://mail.google.com/mail/?authuser=${acct}`
    : 'https://mail.google.com/mail/u/0/'

  // Preferred — resolve the EXACT message by its RFC822 Message-ID (message-
  // accurate; see TARGET note above). These contain `+`/`@`, so they MUST be
  // percent-encoded.
  const mid = (a.message_id_header || '').replace(/^<|>$/g, '').trim()
  if (mid) return `${base}#search/rfc822msgid:${encodeURIComponent(mid)}`

  // Fallback — no Message-ID: open the thread by its Gmail web id. Only
  // conversation-accurate, so it may land on a sibling message of the thread.
  const thr = (a.gmail_thread_id || '').trim()
  if (thr) return `${base}#all/${thr}`

  return null
}
